import type { InviteSignupRequest, LoginRequest, SignupRequest } from '$lib/api/types';

type ValidationResult<T> = { ok: true; request: T } | { ok: false; message: string };

export type LoginFormValues = {
	email: string;
	password: string;
};

export type SignupFormValues = LoginFormValues & {
	name: string;
	passwordConfirm: string;
};

export type InviteSignupFormValues = SignupFormValues & {
	inviteToken: string;
};

export type ForgotPasswordFormValues = {
	email: string;
};

export type PasswordPairValues = {
	newPassword: string;
	confirmPassword: string;
};

export type ChangePasswordFormValues = PasswordPairValues & {
	currentPassword: string;
};

export type PasswordPairResult =
	| { ok: true; password: string }
	| { ok: false; message: string };

export type ChangePasswordResult =
	| { ok: true; request: { currentPassword: string; newPassword: string } }
	| { ok: false; message: string };

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function validateLoginForm(values: LoginFormValues): ValidationResult<LoginRequest> {
	const email = normalizeEmail(values.email);
	if (!email) {
		return { ok: false, message: '이메일을 입력해주세요.' };
	}
	if (!EMAIL_PATTERN.test(email)) {
		return { ok: false, message: '이메일 형식이 올바르지 않습니다.' };
	}
	if (!values.password) {
		return { ok: false, message: '비밀번호를 입력해주세요.' };
	}

	return { ok: true, request: { email, password: values.password } };
}

export function validateSignupForm(values: SignupFormValues): ValidationResult<SignupRequest> {
	const name = values.name.trim();
	if (!name) {
		return { ok: false, message: '이름을 입력해주세요.' };
	}
	if (name.length > 100) {
		return { ok: false, message: '이름은 100자 이하로 입력해주세요.' };
	}

	const login = validateLoginForm(values);
	if (!login.ok) return login;

	const password = validatePasswordPair(
		{
			newPassword: values.password,
			confirmPassword: values.passwordConfirm
		},
		'비밀번호'
	);
	if (!password.ok) return password;

	return {
		ok: true,
		request: {
			name,
			email: login.request.email,
			password: password.password
		}
	};
}

export function validateInviteSignupForm(
	values: InviteSignupFormValues
): ValidationResult<InviteSignupRequest> {
	const inviteToken = values.inviteToken.trim();
	if (!inviteToken) {
		return { ok: false, message: '초대 링크가 유효하지 않습니다.' };
	}

	const signup = validateSignupForm(values);
	if (!signup.ok) return signup;

	return {
		ok: true,
		request: {
			...signup.request,
			inviteToken
		}
	};
}

export function validateForgotPasswordForm(
	values: ForgotPasswordFormValues
): ValidationResult<{ email: string }> {
	const email = normalizeEmail(values.email);
	if (!email) {
		return { ok: false, message: '이메일을 입력해주세요.' };
	}
	if (!EMAIL_PATTERN.test(email)) {
		return { ok: false, message: '이메일 형식이 올바르지 않습니다.' };
	}
	return { ok: true, request: { email } };
}

export function validatePasswordPair(
	values: PasswordPairValues,
	label = '새 비밀번호'
): PasswordPairResult {
	if (!values.newPassword) {
		return { ok: false, message: `${label}를 입력해주세요.` };
	}
	if (!isPasswordStrong(values.newPassword)) {
		return { ok: false, message: `${label}는 8자 이상이며 영문자와 숫자를 포함해야 합니다.` };
	}
	if (values.newPassword !== values.confirmPassword) {
		if (label === '비밀번호') {
			return { ok: false, message: '비밀번호가 일치하지 않습니다.' };
		}
		return { ok: false, message: `${label}와 확인이 일치하지 않습니다.` };
	}
	return { ok: true, password: values.newPassword };
}

export function validateChangePasswordForm(values: ChangePasswordFormValues): ChangePasswordResult {
	if (!values.currentPassword) {
		return { ok: false, message: '현재 비밀번호를 입력해주세요.' };
	}
	const pair = validatePasswordPair(values);
	if (!pair.ok) return pair;
	if (values.currentPassword === pair.password) {
		return { ok: false, message: '새 비밀번호는 현재 비밀번호와 달라야 합니다.' };
	}
	return {
		ok: true,
		request: {
			currentPassword: values.currentPassword,
			newPassword: pair.password
		}
	};
}

export function isPasswordStrong(password: string): boolean {
	return password.length >= 8 && /[a-zA-Z]/.test(password) && /[0-9]/.test(password);
}

function normalizeEmail(value: string): string {
	return value.trim().toLowerCase();
}
